/**
 * Hook for fetching every entity type's property schemas, grouped by
 * `entity_type_name`. Backed by a single batched request that shares its
 * cache key with useAssociatedPropertySchemas — the source of typed
 * formatting, header labels, and degradation checks for lookup columns.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchAllPropertyTypes } from './useFilterSchema';
import type { PropertyTypeSchema } from './usePropertySchema';

interface UseAllPropertySchemasByTypeResult {
  schemasByType: Record<string, Record<string, PropertyTypeSchema>>;
  loading: boolean;
}

/**
 * Fetches property schemas for ALL entity types in one batched request and
 * groups them by `entity_type_name` → `property_type_name`. Robust to
 * heterogeneous (kind-based) association targets, since it keys by concrete
 * `entity_type_name` rather than a single target type.
 *
 * Shares its query key with useAssociatedPropertySchemas so both consumers
 * hit the same cache entry instead of issuing duplicate requests — a
 * per-type fan-out here previously caused org-wide 429s (see
 * useAssociatedPropertySchemas.ts).
 */
export function useAllPropertySchemasByType(
  orgId: string | number | undefined
): UseAllPropertySchemasByTypeResult {
  const query = useQuery({
    queryKey: ['filterSchema', 'allPropertyTypes', String(orgId ?? 0)],
    queryFn: () => fetchAllPropertyTypes(orgId ?? 0),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  const schemasByType = useMemo(() => {
    const map: Record<string, Record<string, PropertyTypeSchema>> = {};
    for (const prop of query.data ?? []) {
      (map[prop.entity_type_name] ??= {})[prop.property_type_name] = prop;
    }
    return map;
  }, [query.data]);

  return {
    schemasByType,
    loading: query.isLoading,
  };
}
